import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { useParticipant } from '../hooks/useParticipant';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import type { Participant } from '../types/participant';
import { auth } from '../firebase';
import { signInWithGoogle } from '../services/auth';

interface ParticipantJoinProps {
  decisionId: string;
  onJoined?: (participant: Participant) => void;
}

export function ParticipantJoin({ decisionId, onJoined }: ParticipantJoinProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const getHourKey = (): string => {
    return `joinCount_${Math.floor(Date.now() / 3600000)}`;
  };
  const { participant, isLoading, requiresAuth, anonymousDisabled, joinDecision } = useParticipant(decisionId);
  const authUserRef = useRef(auth.currentUser);
  const joinAttemptsRef = useRef<number>(0);

  // Load join attempts counter from localStorage on component mount
  useEffect(() => {
    const storedAttempts = localStorage.getItem(getHourKey());
    if (storedAttempts) {
      joinAttemptsRef.current = parseInt(storedAttempts, 10);
    }
  }, []);

  // Check if user has exceeded join rate limit
  const checkJoinRateLimit = (): boolean => {
    const MAX_JOINS_PER_HOUR = 10;
    
    if (joinAttemptsRef.current >= MAX_JOINS_PER_HOUR) {
      setError(`You've joined too many discussions recently. Please try again later.`);
      return false;
    }
    
    return true;
  };

  // Increment join counter - using useCallback to memoize the function
  const incrementJoinCounter = useCallback((): void => {
    joinAttemptsRef.current += 1;
    localStorage.setItem(getHourKey(), joinAttemptsRef.current.toString());
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      authUserRef.current = user;
      
      // Only proceed if component is still mounted
      if (!isMounted) return;
      
      // Try to auto-join after auth state changes
      if (user && !participant && !isLoading) {
        try {
          // Even auto-join should respect rate limits
          if (!checkJoinRateLimit()) {
            return;
          }
          
          const newParticipant = await joinDecision();
          incrementJoinCounter();
          if (isMounted && onJoined) {
            onJoined(newParticipant);
          }
        } catch (err) {
          if (isMounted) {
            setError(err instanceof Error ? err.message : 'Failed to join discussion');
          }
        }
      }
    });
  
    // Initial check for already authenticated users
    const autoJoinOrNotify = async () => {
      if (participant && onJoined) {
        onJoined(participant);
        return;
      }
  
      if (authUserRef.current && !participant && !isLoading) {
        try {
          // Even auto-join should respect rate limits
          if (!checkJoinRateLimit()) {
            return;
          }
          
          const newParticipant = await joinDecision();
          incrementJoinCounter();
          if (isMounted) {
            onJoined?.(newParticipant);
          }
        } catch (err) {
          if (isMounted) {
            setError(err instanceof Error ? err.message : 'Failed to join discussion');
          }
        }
      }
    };
  
    autoJoinOrNotify();
    
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [participant, isLoading, joinDecision, onJoined, incrementJoinCounter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
  
    try {
      if (!name.trim()) {
        setError('Please enter your name');
        return;
      }
      
      // Check rate limit before attempting to join
      if (!checkJoinRateLimit()) {
        return;
      }
  
      const newParticipant = await joinDecision(name.trim());
      incrementJoinCounter();
      onJoined?.(newParticipant);
    } catch (err) {
      if (err instanceof Error && 
          err.message.includes('maximum number of anonymous participants')) {
        setError('This discussion has reached the participant limit for anonymous users. Please sign in with Google to join.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to join discussion');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      // Also check rate limit for Google sign-in attempts
      if (!checkJoinRateLimit()) {
        return;
      }
      
      await signInWithGoogle();
      // Only increment on successful sign-in
      incrementJoinCounter();
    } catch (err) {
      setError('Failed to sign in with Google');
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto my-6">
        <CardContent className="p-6">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (participant) {
    return null;
  }

  // Show Google-only UI if anonymous participation is disabled or auth is required
  if (anonymousDisabled || requiresAuth) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Share Your View</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">Sign in with Google to join this discussion.</p>
            <Button 
              onClick={handleGoogleSignIn}
              className="w-full bg-primary hover:bg-primary/90"
            >
              Sign in with Google
            </Button>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto my-6">
      <CardHeader>
        <CardTitle>Share Your View</CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-6">
          {/* Google Sign In Option */}
          <div>
            <Button 
              onClick={handleGoogleSignIn}
              className="w-full bg-primary hover:bg-primary/90"
            >
              Sign in with Google
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                or continue as guest
              </span>
            </div>
          </div>

          {/* Anonymous Join Form */}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label 
                htmlFor="name-input" 
                className="text-sm font-medium mb-2 block"
              >
                Your Name
              </label>
              <input
                id="name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter your name"
              />
            </div>
            <Button 
              type="submit" 
              variant="secondary"
              className="w-full"
            >
              Continue as Guest
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}